"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  LegacyGame,
  POSE_CELLS,
  getPoseCells,
  type GameEvent,
  type InputControl,
  type PieceId,
} from "../../lib/game";
import { applyInputToActiveGame, quantizeInputTransitionTick } from "../battle-boundaries";
import { useLegacyAudio } from "../hooks/useLegacyAudio";
import { useLegacyKeyboard, type KeyBindings } from "../hooks/useLegacyKeyboard";
import { BattleBoard } from "./BattleBoard";
import { type BoardView } from "./BattleArena";
import { NextPiece } from "./NextPiece";
import { TouchControls, type LegacyAction } from "./TouchControls";
import { AdSlot } from "./AdSlot";

type PracticeModeProps = {
  keyBindings: KeyBindings;
  onBack: () => void;
};

type QueuedPracticeInput = {
  applyBeforeTick: number;
  control: InputControl;
  held: boolean;
  sequence: number;
};

const ACTION_TO_INPUT: Readonly<Record<LegacyAction, InputControl>> = {
  ccw: "rotateCCW",
  cw: "rotateCW",
  left: "left",
  right: "right",
  softDrop: "softDrop",
  hardDrop: "hardDrop",
};

function nextCells(piece: number): ReadonlyArray<readonly [number, number]> {
  if (piece < 1 || piece > 7) return [];
  return POSE_CELLS[piece as PieceId][0].map(({ dx, dy }) => [dx, dy] as const);
}

function viewFromGame(game: LegacyGame): BoardView {
  const active = game.active;
  return {
    active: getPoseCells(active).map(({ x, y }) => ({ x, y, piece: active.piece })),
    blockIndex: game.blockIndex,
    board: game.board,
    nextCells: nextCells(game.nextPiece),
    nextPiece: game.nextPiece,
    pending: 0,
    received: 0,
    topOut: game.topOut,
  };
}

function newPracticeSeed(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function PracticeMode({ keyBindings, onBack }: PracticeModeProps) {
  const initialGame = useState(
    () => new LegacyGame({ matchId: `practice:${Date.now()}`, seed: newPracticeSeed() }),
  )[0];
  const [view, setView] = useState<BoardView>(() => viewFromGame(initialGame));
  const [running, setRunning] = useState(true);
  const [gameOver, setGameOver] = useState(false);
  const [lines, setLines] = useState(0);
  const [pieces, setPieces] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [status, setStatus] = useState("練習中");

  const gameRef = useRef<LegacyGame | undefined>(initialGame);
  const runningRef = useRef(true);
  const originTimeRef = useRef(0);
  const inputSequenceRef = useRef(0);
  const inputOrderRef = useRef(0);
  const queuedInputsRef = useRef<QueuedPracticeInput[]>([]);
  const requestedInputsRef = useRef<Record<InputControl, boolean>>({
    left: false,
    right: false,
    rotateCCW: false,
    rotateCW: false,
    softDrop: false,
    hardDrop: false,
  });
  const { playLock, unlock } = useLegacyAudio();

  const processEvents = useCallback((events: readonly GameEvent[]) => {
    for (const event of events) {
      if (event.type !== "lock") {
        if (event.type === "topout") {
          runningRef.current = false;
          setRunning(false);
          setGameOver(true);
          setStatus("GAME OVER");
        }
        continue;
      }
      playLock(event.clearedLines);
      setPieces((value) => value + 1);
      if (event.clearedLines > 0) setLines((value) => value + event.clearedLines);
    }
  }, [playLock]);

  const advanceGame = useCallback((targetTick: number) => {
    const game = gameRef.current;
    if (!game || targetTick < game.tick) return;

    while (queuedInputsRef.current[0]?.applyBeforeTick <= targetTick && !game.topOut) {
      const queued = queuedInputsRef.current.shift();
      if (!queued) break;
      const tickBeforeSample = Math.max(game.tick, queued.applyBeforeTick - 1);
      if (tickBeforeSample > game.tick) processEvents(game.stepTo(tickBeforeSample));
      if (game.topOut) break;
      const inputSequence = inputSequenceRef.current + 1;
      if (!applyInputToActiveGame(game, queued.control, queued.held, inputSequence)) break;
      inputSequenceRef.current = inputSequence;
    }

    if (!game.topOut && targetTick > game.tick) processEvents(game.stepTo(targetTick));
  }, [processEvents]);

  const resetGame = useCallback(() => {
    const game = new LegacyGame({ matchId: `practice:${Date.now()}`, seed: newPracticeSeed() });
    gameRef.current = game;
    runningRef.current = true;
    originTimeRef.current = performance.now();
    inputSequenceRef.current = 0;
    inputOrderRef.current = 0;
    queuedInputsRef.current = [];
    requestedInputsRef.current = {
      left: false,
      right: false,
      rotateCCW: false,
      rotateCW: false,
      softDrop: false,
      hardDrop: false,
    };
    setView(viewFromGame(game));
    setRunning(true);
    setGameOver(false);
    setLines(0);
    setPieces(0);
    setSeconds(0);
    setStatus("練習中");
  }, []);

  useEffect(() => {
    let frame = 0;
    const render = () => {
      const game = gameRef.current;
      if (game && runningRef.current && !game.topOut) {
        const targetTick = Math.floor((performance.now() - originTimeRef.current) * 0.12);
        if (targetTick > game.tick) advanceGame(targetTick);
        setView(viewFromGame(game));
        setSeconds(Math.floor(game.tick / 120));
      }
      frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, [advanceGame]);

  const handleInput = useCallback((action: LegacyAction, held: boolean) => {
    void unlock();
    if (!runningRef.current || gameRef.current?.topOut) return;
    const control = ACTION_TO_INPUT[action];
    if (requestedInputsRef.current[control] === held) return;
    requestedInputsRef.current[control] = held;
    const game = gameRef.current;
    if (!game) return;
    const applyBeforeTick = quantizeInputTransitionTick(
      game.startTick,
      game.tick,
      control,
      queuedInputsRef.current,
    );
    queuedInputsRef.current.push({
      applyBeforeTick,
      control,
      held,
      sequence: ++inputOrderRef.current,
    });
    queuedInputsRef.current.sort(
      (left, right) => left.applyBeforeTick - right.applyBeforeTick || left.sequence - right.sequence,
    );
  }, [unlock]);

  useLegacyKeyboard(running && !gameOver, handleInput, "A", keyBindings);

  useEffect(() => {
    originTimeRef.current = performance.now();
  }, []);

  const togglePause = async () => {
    await unlock();
    if (!gameRef.current || gameOver) return;
    if (runningRef.current) {
      runningRef.current = false;
      setRunning(false);
      setStatus("一時停止中");
    } else {
      originTimeRef.current = performance.now() - gameRef.current.tick / 0.12;
      runningRef.current = true;
      setRunning(true);
      setStatus("練習中");
    }
  };

  const handleRestart = async () => {
    await unlock();
    resetGame();
  };

  return (
    <main className="mode-shell practice-shell">
      <header className="mode-header">
        <button className="back-button" onClick={onBack} type="button">← メニュー</button>
        <span className="mode-header-label">SOLO PRACTICE</span>
        <span className="practice-status"><span className="status-dot" /> {status}</span>
      </header>

      <div className="practice-layout">
        <section aria-label="1人練習の盤面" className="practice-arena">
          <BattleBoard
            active={view.active}
            activeBlockIndex={view.blockIndex}
            board={view.board}
            controlsDisabled={!running || gameOver}
            label="PRACTICE"
            local
            onInput={handleInput}
            topOut={view.topOut}
          />
          <aside className="practice-info">
            <NextPiece cells={view.nextCells} label="NEXT" piece={view.nextPiece} />
            <div className="practice-metrics">
              <div className="metric"><span className="metric-label">TIME</span><strong>{String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}</strong></div>
              <div className="metric"><span className="metric-label">LINES</span><strong>{lines}</strong></div>
              <div className="metric"><span className="metric-label">PIECES</span><strong>{pieces}</strong></div>
            </div>
            <p className="practice-hint">左右スワイプ：移動<br />タップ：回転（左半分／右半分）<br />下へドラッグ：ソフトドロップ<br />下フリック：ハードドロップ</p>
          </aside>
        </section>

        <div className="practice-controls-row">
          <div className="practice-actions">
            <button className="secondary-button" onClick={() => void togglePause()} type="button">
              {running ? "一時停止" : gameOver ? "ゲームオーバー" : "再開"}
            </button>
            <button className="chrome-button" onClick={() => void handleRestart()} type="button">もう一度</button>
          </div>
        </div>
        <TouchControls disabled={!running || gameOver} onInput={handleInput} side="left" variant="practice" />

        <AdSlot format="horizontal" label="練習画面の広告スペース" />
      </div>
    </main>
  );
}
